<template>
  <div data-view="scheduled-pins">
    <router-view />

    <layout-logged-frame title="Pins" has-before>
      <template v-slot:header-actions>
        <v-btn
          color="primary"
          @click="$router.push({ name: addRoute })"
        >
          <v-icon>add</v-icon>
          {{ $t('add_new_pin') }}
        </v-btn>
      </template>

      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="tabsItemsCounter"
        />
      </template>

      <template v-slot:content>
        <PinsFilters @filter="onFilter" @clearFilters="onClearFilters" />

        <table-base
          :items="pins"
          :columns="columns"
          :pages="totalPages"
          :pagination="pagination"
          :title="tableTitle"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectPin"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'start_date'">
              <PinDate
                v-if="hasDate(scope.cell.item.start_date)"
                :date="scope.cell.item.start_date"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'end_date'">
              <PinDate
                v-if="hasDate(scope.cell.item.end_date)"
                :date="scope.cell.item.end_date"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'deal'">
              <PinThread :thread="scope.cell.item" />
            </template>

            <template v-else-if="scope.cell.column.value === 'status'">
              <StatusPanel
                table-group="pins"
                :status="scope.cell.item.status.name"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'page'">
              <PinPage :pin="scope.cell.item" />
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import PinDate from '../../components/panels/Date';
  import StatusPanel from '../../components/panels/Status';
  import PinPage from '../../components/panels/pins/Page';
  import PinThread from '../../components/panels/Thread';
  import PinsFilters from '../../components/forms/filters/Pins';
  import { isStr, extractNestedProp } from '@/utils';
  import columns from '../../config/pins/columns';
  import navigationTabs from '../../config/pins/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'

  export default {
    components: {
      NavigationTabs,
      PinsFilters,
      PinDate,
      StatusPanel,
      PinPage,
      PinThread,
    },
    data() {
      return {
        navigationTabs,
        pagination: {
          page: 1,
          rowsPerPage: import.meta.env.VUE_APP_API_MAX_ITEMS_PER_PAGE,
          totalItems: 0,
          search: '',
          sortBy: 'name',
          order: 'asc' ,
        },
        query: {},
        totalPages: 0,
        columns,
        isLoading: false,
      };
    },
    computed: {
      tabsItemsCounter() {
        if(this.$route.name === 'running-pins') {
          return [null, this.pagination.totalItems];
        }
        return [this.pagination.totalItems];
      },
      addRoute() {
        return this.$route.name === 'running-pins' ? 'running-pins-new' : 'pins-new';
      },
      pins() {
        return this.$store.getters['pins/items'];
      },
      pinsMeta() {
        return this.$store.getters['pins/itemsMeta'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      tableTitle() {
        return `${this.pagination.totalItems || 0} pins`;
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        const parentRoutes = ['pins', 'running-pins'];
        const childRoutes = ['pins-new', 'pins-edit', 'running-pins-new', 'running-pins-edit'];

        // Reload when switching between pins/running-pins or returning from a child route
        if (
          parentRoutes.includes(to.name) &&
          (parentRoutes.includes(from.name) || (to.query?.status === 'success' && childRoutes.includes(from.name)))
        ) {
          this.getPins(this.query);
        }
      },
    },
    methods: {
      getPins({ page, countries } = {}) {
        // todo check if order and sortBy needed
        const query = {
          page,
          perPage: this.pagination.rowsPerPage,
          countries,
        };

        if (this.$route.name === 'running-pins') {
          query.type = 'running'
        }

        this.isLoading = true;
        return this.$store
          .dispatch('pins/getItems', query)
          .then((res) => {
            const { total, current_page, last_page } = this.pinsMeta;
            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;
            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.getPins(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getPins(nextQuery);
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...this.getSearchQuery(filters)
        };

        this.getPins(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        const nextQuery = { sort: sortBy };

        this.query = nextQuery;
        this.getPins(nextQuery);
      },
      getSearchQuery({ countries }) {
        const query = {};
        query.countries = countries;

        return query;
      },
      selectPin(pin) {
        this.$store.dispatch('pins/setActiveItem', pin);
        const editRoute = this.$route.name === 'running-pins' ? 'running-pins-edit' : 'pins-edit';
        this.$router.push({ name: editRoute, params: { id: pin.id } });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
      hasDate(date) {
        return isStr(date);
      },
    },
  };
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='scheduled-pins'] {
  .v-datatable {
    min-width: $table-min-width;
  }

  [data-component-group='lists'] {
    &[data-component='comments'] {
      max-width: 350px;
    }
  }

  [data-component-group='panels'] {
    &[data-component='status'] {
      font-size: $text-size-sm;
      font-weight: bold;
    }
  }
}
</style>
